import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Github, X, Search, ChevronDown, Globe, Layers, Cpu, Server, Database, Brain, Zap, Code, Clock, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';
import { portfolioData } from '../data/mock';

const tagColors = {
  'Enterprise Software': 'from-blue-500 to-indigo-500',
  'AI & Automation': 'from-violet-500 to-purple-500',
  'DevOps Automation': 'from-cyan-500 to-teal-500',
  'Infrastructure Engineering': 'from-emerald-500 to-green-500',
  'System Integration': 'from-amber-500 to-orange-500',
  'AI Engineering': 'from-rose-500 to-pink-500',
  'Generative AI': 'from-violet-500 to-purple-500',
  'Full Stack': 'from-indigo-500 to-blue-500',
  'Database Engineering': 'from-emerald-500 to-green-500',
  'Automation': 'from-amber-500 to-orange-500',
  'Web Development': 'from-sky-500 to-blue-500',
  'DevOps': 'from-cyan-500 to-teal-500'
};

const modalTabs = ['Overview', 'Architecture', 'Features', 'Impact', 'Lessons'];

const allProjects = [...portfolioData.projects.featured, ...portfolioData.projects.archive];

const Projects = () => {
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modalTab, setModalTab] = useState('Overview');
  const [mode, setMode] = useState('featured');
  const [visibleCount, setVisibleCount] = useState(6);
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  const currentList = mode === 'featured' ? portfolioData.projects.featured : portfolioData.projects.archive;
  const filtered = searchQuery
    ? currentList.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.technologies.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())) ||
        p.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentList;
  const displayed = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const loadMore = () => setVisibleCount(prev => Math.min(prev + 6, filtered.length));

  const switchMode = (newMode) => {
    setMode(newMode);
    setVisibleCount(6);
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setVisibleCount(6);
  };

  return (
    <section id="projects" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <Globe size={12} /><span>Project Portfolio</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Systems I've <span className="gradient-text">Built</span>
            </h2>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex gap-2">
              <button onClick={() => switchMode('featured')}
                className={`px-5 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${mode === 'featured' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'glass text-gray-400 hover:text-white'}`}>
                Featured Projects ({portfolioData.projects.featured.length})
              </button>
              <button onClick={() => switchMode('archive')}
                className={`px-5 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${mode === 'archive' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'glass text-gray-400 hover:text-white'}`}>
                Engineering Archive ({portfolioData.projects.archive.length})
              </button>
            </div>
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
              <input type="text" value={searchQuery} onChange={handleSearch} placeholder="Search by title, tech, category..."
                className="w-full pl-9 pr-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-gray-300 text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 transition-all duration-300" />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">No projects match your search.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayed.map((project, i) => {
                  const tagColor = tagColors[project.tag] || 'from-indigo-500 to-purple-500';
                  return (
                    <div key={project.id} className="glass rounded-2xl overflow-hidden group cursor-pointer hover:border-indigo-500/20 transition-all duration-500 hover:-translate-y-1"
                      onClick={() => { setSelected(project); setModalTab('Overview'); }}
                      style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'translateY(0)' : 'translateY(20px)',
                        transitionDelay: `${i * 80}ms`,
                        transitionProperty: 'all',
                        transitionDuration: '0.6s',
                        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                      }}>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className={`px-3 py-1 rounded-full bg-gradient-to-r ${tagColor} text-white text-xs font-medium`}>{project.tag}</span>
                          <span className="text-gray-600 text-xs font-mono">{project.year}</span>
                        </div>
                        <h3 className="text-white font-bold text-base mb-2 group-hover:gradient-text transition-all duration-300">{project.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2">{project.overview}</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {project.technologies.slice(0, 4).map(t => (
                            <span key={t} className="px-2 py-0.5 bg-white/5 rounded-md text-gray-500 text-xs font-mono">{t}</span>
                          ))}
                          {project.technologies.length > 4 && (
                            <span className="px-2 py-0.5 bg-indigo-500/10 rounded-md text-indigo-400 text-xs font-mono">+{project.technologies.length - 4}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-mono ${
                            project.status === 'Production' ? 'bg-green-500/10 text-green-400' :
                            project.status === 'Active' ? 'bg-blue-500/10 text-blue-400' :
                            project.status === 'Experimental' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-gray-500/10 text-gray-400'
                          }`}>{project.status}</span>
                          {project.impact && <span className="text-indigo-400 text-xs font-mono truncate max-w-[180px]">{project.impact}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-10">
                  <button onClick={loadMore}
                    className="group px-8 py-3.5 glass rounded-xl text-gray-300 text-sm font-medium hover:text-white hover:border-indigo-500/30 transition-all duration-300 flex items-center space-x-2">
                    <span>View More Projects ({filtered.length - visibleCount} remaining)</span>
                    <ChevronDown size={16} className="group-hover:translate-y-0.5 transition-transform" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0a0a0f] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{selected.title}</h2>
                <div className="flex items-center space-x-3 mt-1">
                  <span className={`px-2.5 py-0.5 rounded-full bg-gradient-to-r ${tagColors[selected.tag] || 'from-indigo-500 to-purple-500'} text-white text-xs font-medium`}>{selected.tag}</span>
                  <span className="text-gray-600 text-xs font-mono">{selected.year}</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-mono ${
                    selected.status === 'Production' ? 'bg-green-500/10 text-green-400' :
                    selected.status === 'Active' ? 'bg-blue-500/10 text-blue-400' : 'bg-gray-500/10 text-gray-400'
                  }`}>{selected.status}</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-white/5 rounded-xl transition-colors">
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div className="border-b border-white/5">
              <div className="flex overflow-x-auto">
                {modalTabs.map(tab => (
                  <button key={tab} onClick={() => setModalTab(tab)}
                    className={`px-6 py-3 text-xs font-medium transition-all duration-300 whitespace-nowrap ${
                      modalTab === tab ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-500 hover:text-gray-300'
                    }`}>{tab}</button>
                ))}
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {modalTab === 'Overview' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-3">Overview</h3>
                    <p className="text-gray-300 leading-relaxed">{selected.overview}</p>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-3">Problem Statement</h3>
                    <div className="glass rounded-xl p-5 border border-amber-500/10">
                      <div className="flex items-start space-x-3">
                        <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-300 leading-relaxed">{selected.problem}</p>
                      </div>
                    </div>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-3">Solution</h3>
                    <p className="text-gray-300 leading-relaxed">{selected.solution}</p>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Technologies</h3>
                    <div className="flex flex-wrap gap-2">
                      {selected.technologies.map(t => (
                        <span key={t} className="px-3 py-1.5 bg-white/5 rounded-lg text-gray-300 text-sm font-mono">{t}</span>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {modalTab === 'Architecture' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-3">Architecture Flow</h3>
                    <div className="glass rounded-xl p-5 font-mono text-sm text-gray-400 leading-relaxed">
                      {selected.architecture}
                    </div>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">System Design</h3>
                    <div className="space-y-3">
                      {selected.architecture.split('→').map((part, i) => (
                        <div key={i} className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02]">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center text-indigo-400 text-xs font-mono">{i + 1}</div>
                          <span className="text-gray-400 text-sm">{part.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {modalTab === 'Features' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Key Features</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {selected.features.map((f, i) => (
                        <div key={i} className="flex items-start space-x-3 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300">
                          <CheckCircle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{f}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Challenges Solved</h3>
                    <div className="space-y-3">
                      {selected.challenges.map((c, i) => (
                        <div key={i} className="flex items-start space-x-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/10">
                          <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{c}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {modalTab === 'Impact' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Measurable Impact</h3>
                    <div className="glass rounded-2xl p-6 border border-indigo-500/10">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                          <Zap size={22} className="text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-white font-semibold text-lg">{selected.impact}</p>
                          <p className="text-gray-500 text-sm mt-1">Real-world outcome of this system</p>
                        </div>
                      </div>
                    </div>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Lessons Learned</h3>
                    <div className="glass rounded-2xl p-6 border border-amber-500/10">
                      <div className="flex items-start space-x-4">
                        <BookOpen size={18} className="text-amber-400 mt-1 flex-shrink-0" />
                        <p className="text-gray-300 italic leading-relaxed">{selected.lessons}</p>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {modalTab === 'Lessons' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Engineering Insights</h3>
                    <div className="glass rounded-2xl p-6 border border-amber-500/10">
                      <div className="flex items-start space-x-4">
                        <BookOpen size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                        <p className="text-gray-300 italic leading-relaxed text-lg">{selected.lessons}</p>
                      </div>
                    </div>
                  </section>
                  <section>
                    <h3 className="text-indigo-400 text-xs font-mono uppercase tracking-wider mb-4">Key Takeaways</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {selected.challenges.map((c, i) => (
                        <div key={i} className="flex items-start space-x-3 p-4 rounded-xl bg-white/[0.02]">
                          <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-mono flex-shrink-0">{i + 1}</div>
                          <span className="text-gray-400 text-sm">{c}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}
            </div>

            {selected.githubUrl && (
              <div className="border-t border-white/5 p-6">
                <a href={selected.githubUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-5 py-2.5 glass rounded-xl text-gray-300 hover:text-white hover:border-indigo-500/30 transition-all duration-300">
                  <Github size={16} /><span className="text-sm">View Source Code</span>
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
